import { onMount } from 'solid-js';
import { Header } from '~/components/Header';
import { Footer } from '~/components/Footer';
import { Hero } from '~/sections/Hero';
import { ProductsSlider } from '~/sections/ProductsSlider';
import { Supplies } from '~/sections/Supplies';
import { Manufacturers } from '~/sections/Manufacturers';
import { WhyUs } from '~/sections/WhyUs';
import { Workflow } from '~/sections/Workflow';
import { SeoBlock } from '~/sections/SeoBlock';
import { LeadForm } from '~/sections/LeadForm';
import { Contacts } from '~/sections/Contacts';
import { applySeo, injectJsonLd } from '~/lib/seo';
import { useReveal } from '~/lib/reveal';
import { contacts, siteConfig } from '~/data/config';
import { products } from '~/data/products';

export function HomePage() {
  const scrollToLead = () => {
    document.getElementById('lead')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  onMount(() => {
    applySeo({
      title:
        'Купить двигатели и агрегаты для грузовиков и спецтехники в Екатеринбурге — Концепт 13',
      description:
        'Продажа и поставка двигателей Caterpillar, Cummins, Mercedes-Benz, John Deere для грузовиков и спецтехники. Подбор по VIN за 1 рабочий день. Гарантия. Доставка по России из Екатеринбурга.',
      path: '/',
      ogType: 'website',
    });

    if (window.location.hash) {
      const id = window.location.hash;
      setTimeout(() => {
        document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  });

  useReveal();

  injectJsonLd('ld-organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: contacts.legalName,
    alternateName: siteConfig.brand,
    url: siteConfig.domain,
    logo: `${siteConfig.domain}/logo.jpg`,
    contactPoint: contacts.phones.map((p) => ({
      '@type': 'ContactPoint',
      telephone: p.tel,
      contactType: p.label === 'Офис' ? 'customer service' : 'sales',
      areaServed: 'RU',
      availableLanguage: 'ru',
    })),
    address: {
      '@type': 'PostalAddress',
      streetAddress: contacts.address.street,
      addressLocality: contacts.address.city,
      postalCode: contacts.address.postalCode,
      addressCountry: 'RU',
    },
    email: contacts.email,
  });

  injectJsonLd('ld-localbusiness', {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: contacts.legalName,
    url: siteConfig.domain,
    telephone: contacts.phones[0].tel,
    email: contacts.email,
    image: `${siteConfig.domain}${siteConfig.ogImage}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: contacts.address.street,
      addressLocality: contacts.address.city,
      postalCode: contacts.address.postalCode,
      addressCountry: 'RU',
    },
    areaServed: 'RU',
    priceRange: '₽₽₽',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  });

  injectJsonLd('ld-products', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: products.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${siteConfig.domain}/products/${p.slug}`,
      name: p.fullName,
    })),
  });

  injectJsonLd('ld-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.brand,
    alternateName: contacts.legalName,
    url: siteConfig.domain,
    inLanguage: 'ru-RU',
    publisher: {
      '@type': 'Organization',
      name: contacts.legalName,
    },
  });

  injectJsonLd('ld-service', {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Поставка двигателей и агрегатов для грузовиков и спецтехники',
    provider: {
      '@type': 'Organization',
      name: contacts.legalName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: contacts.address.street,
        addressLocality: contacts.address.city,
        postalCode: contacts.address.postalCode,
        addressCountry: 'RU',
      },
      telephone: contacts.phones[0].tel,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Россия',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Каталог двигателей',
      itemListElement: products.map((p) => ({
        '@type': 'Offer',
        url: `${siteConfig.domain}/products/${p.slug}`,
        priceCurrency: 'RUB',
        price: p.priceValue,
        availability:
          p.status === 'in_stock'
            ? 'https://schema.org/InStock'
            : 'https://schema.org/PreOrder',
        itemOffered: {
          '@type': 'Product',
          name: p.fullName,
          brand: { '@type': 'Brand', name: p.brand },
        },
      })),
    },
  });

  return (
    <>
      <Header onRequestClick={scrollToLead} />
      <main>
        <Hero onRequestClick={scrollToLead} />
        <ProductsSlider onRequestClick={scrollToLead} />
        <Supplies onRequestClick={scrollToLead} />
        <Manufacturers />
        <WhyUs />
        <Workflow onRequestClick={scrollToLead} />
        <SeoBlock />
        <LeadForm />
        <Contacts />
      </main>
      <Footer />
    </>
  );
}
